import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Loader2,
  RefreshCw,
  MessageSquare,
  Users,
  Lightbulb,
  Bug,
  AlertTriangle,
  Bot,
} from "lucide-react";

const BotInsightsSection = ({ API_URL, getAuthHeaders }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("suggestions");

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/bot-insights`, { headers: getAuthHeaders() });
      setInsights(response.data);
    } catch (error) {
      console.error("Failed to load bot insights:", error);
      toast.error("Failed to load bot insights");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const stats = insights?.stats || {};
  const categories = [
    { key: "suggestions", label: "Suggestions & Feature Requests", icon: Lightbulb, color: "text-amber-500", bgColor: "bg-amber-500/10", borderColor: "border-amber-500/20", data: insights?.suggestions || [] },
    { key: "issues", label: "Issues & Bugs", icon: Bug, color: "text-red-500", bgColor: "bg-red-500/10", borderColor: "border-red-500/20", data: insights?.issues || [] },
    { key: "complaints", label: "Complaints", icon: AlertTriangle, color: "text-orange-500", bgColor: "bg-orange-500/10", borderColor: "border-orange-500/20", data: insights?.complaints || [] },
  ];

  const activeCat = categories.find(c => c.key === activeCategory);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <MessageSquare className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold">{stats.total_messages || 0}</p>
            <p className="text-xs text-muted-foreground">Total Messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold">{stats.unique_sessions || 0}</p>
            <p className="text-xs text-muted-foreground">Sessions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Lightbulb className="w-5 h-5 mx-auto mb-1 text-amber-500" />
            <p className="text-2xl font-bold">{stats.suggestion_count || 0}</p>
            <p className="text-xs text-muted-foreground">Suggestions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Bug className="w-5 h-5 mx-auto mb-1 text-red-500" />
            <p className="text-2xl font-bold">{stats.issue_count || 0}</p>
            <p className="text-xs text-muted-foreground">Issues</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-orange-500" />
            <p className="text-2xl font-bold">{stats.complaint_count || 0}</p>
            <p className="text-xs text-muted-foreground">Complaints</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              Bot Conversation Insights
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchInsights} className="gap-2" data-testid="refresh-bot-insights-btn">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
          <CardDescription>User feedback extracted from chatbot conversations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  activeCategory === cat.key
                    ? `${cat.bgColor} ${cat.color} ${cat.borderColor}`
                    : 'bg-muted/30 text-muted-foreground border-transparent hover:bg-muted/50'
                }`}
                data-testid={`bot-tab-${cat.key}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
                <Badge variant="secondary" className="ml-1">{cat.data.length}</Badge>
              </button>
            ))}
          </div>

          {activeCat && activeCat.data.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <activeCat.icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No {activeCat.label.toLowerCase()} yet</p>
              <p className="text-sm mt-1">Messages from bot conversations will appear here once users interact with QueryBot</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {activeCat?.data.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`p-4 rounded-lg border ${activeCat.borderColor} ${activeCat.bgColor}`}
                  data-testid={`bot-insight-${activeCategory}-${idx}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium mb-1">User message:</p>
                      <p className="text-sm text-foreground">{msg.user_message}</p>
                      {msg.bot_response && (
                        <div className="mt-2 pt-2 border-t border-border/50">
                          <p className="text-xs text-muted-foreground mb-1">Bot response:</p>
                          <p className="text-xs text-muted-foreground">
                            {typeof msg.bot_response === 'string' ? msg.bot_response.slice(0, 200) : ''}
                            {typeof msg.bot_response === 'string' && msg.bot_response.length > 200 ? '...' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Session: {msg.session_id?.slice(0, 8)}...</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BotInsightsSection;
